import React, { useState } from 'react';
import { X, ChevronLeft, ChevronRight, Calendar, Users, MapPin, Play, Image } from 'lucide-react';

const PhotoModal = ({ event, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const mediaItems = event.media && event.media.length > 0
    ? event.media
    : (event.images || []).map(url => ({ type: 'image', url }));

  const currentItem = mediaItems[currentIndex];

  const nextPhoto = () => {
    setCurrentIndex((prev) => (prev + 1) % mediaItems.length);
  };

  const prevPhoto = () => {
    setCurrentIndex((prev) => (prev - 1 + mediaItems.length) % mediaItems.length);
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Regex mise à jour
  const getYouTubeId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtu\.be\/|youtube\.com(?:\/embed\/|\/v\/|\/watch\?v=|\/watch\?.+&v=|\/shorts\/))([\w-]{11})/);
    return match ? match[1] : null;
  };

  const getYouTubeThumbnail = (url) => {
     const id = getYouTubeId(url);
     return id ? `https://img.youtube.com/vi/${id}/0.jpg` : null;
  };

  const getVideoType = (fileName, url) => {
    if (fileName) {
        if (fileName.toLowerCase().endsWith('.mov')) return 'video/quicktime';
        if (fileName.toLowerCase().endsWith('.mp4')) return 'video/mp4';
        if (fileName.toLowerCase().endsWith('.webm')) return 'video/webm';
    }
    if (url.toLowerCase().endsWith('.mov')) return 'video/quicktime';
    return 'video/mp4';
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
      onClick={handleBackdropClick}
    >
      <div className="relative w-full max-w-6xl">
        {/* Header */}
        <div className="bg-white/10 backdrop-blur-sm rounded-t-lg p-4 mb-2">
          <div className="flex justify-between items-start">
            <div className="text-white">
              <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'var(--font-serif)' }}>
                {event.clientName}
              </h2>
              <div className="flex flex-wrap gap-4 text-sm">
                <div className="flex items-center">
                  <Calendar size={16} className="mr-2" />
                  <span>{event.date}</span>
                </div>
                <div className="flex items-center">
                  <Users size={16} className="mr-2" />
                  <span>{event.guestCount}</span>
                </div>
                {event.location && (
                  <div className="flex items-center">
                    <MapPin size={16} className="mr-2" />
                    <span>{event.location}</span>
                  </div>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-white/20 p-2 rounded-full transition-colors"
              aria-label="Fermer"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="relative bg-black rounded-lg overflow-hidden flex items-center justify-center bg-zinc-900">
          
          {currentItem?.type === 'video' ? (
            <div className="w-full h-[70vh] flex items-center justify-center">
              {getYouTubeId(currentItem.url) ? (
                // YOUTUBE (Normal ou Shorts)
                <iframe
                  src={`https://www.youtube.com/embed/${getYouTubeId(currentItem.url)}`}
                  title={currentItem.title || `Vidéo ${currentIndex + 1}`}
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                  allowFullScreen
                ></iframe>
              ) : (
                // VIDEO LOCALE
                <video 
                  key={currentItem.url}
                  controls 
                  autoPlay 
                  playsInline
                  className="max-w-full max-h-full"
                >
                  <source src={currentItem.url} type={getVideoType(currentItem.fileName, currentItem.url)} />
                  Votre navigateur ne supporte pas la lecture de vidéos.
                </video>
              )}
            </div>
          ) : (
            // IMAGE
            <img
              src={currentItem?.url}
              alt={`${event.clientName} - Photo ${currentIndex + 1}`}
              className="w-full h-[70vh] object-contain"
            />
          )}

          {/* Navigation Buttons */}
          {mediaItems.length > 1 && (
            <>
              <button
                onClick={prevPhoto}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white p-3 rounded-full transition-colors"
                aria-label="Précédent"
              >
                <ChevronLeft size={24} />
              </button>
              <button
                onClick={nextPhoto}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white p-3 rounded-full transition-colors"
                aria-label="Suivant"
              >
                <ChevronRight size={24} />
              </button>
            </>
          )}

          {/* Counter */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm flex items-center gap-2">
            {currentItem?.type === 'video' ? (
              <Play size={14} fill="currentColor" />
            ) : (
              <Image size={14} />
            )}
            {currentIndex + 1} / {mediaItems.length}
          </div>
        </div>

        {/* Thumbnail Strip */}
        {mediaItems.length > 1 && (
          <div className="mt-2 overflow-x-auto">
            <div className="flex gap-2 p-2 bg-white/10 backdrop-blur-sm rounded-b-lg">
              {mediaItems.map((item, index) => {
                 let thumbContent;
                 const ytId = getYouTubeId(item.url);

                 if (item.type === 'video') {
                     if (ytId) {
                         // YouTube Thumb
                         thumbContent = (
                             <>
                                <img src={`https://img.youtube.com/vi/${ytId}/0.jpg`} className="w-full h-full object-cover opacity-80" alt="Video thumb" />
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <Play size={24} className="text-white drop-shadow-md" fill="currentColor" />
                                </div>
                             </>
                         );
                     } else {
                         // Local Thumb
                         thumbContent = (
                            <div className="w-full h-full bg-gray-800 flex items-center justify-center">
                                <Play size={24} className="text-white" fill="currentColor" />
                            </div>
                         );
                     }
                 } else {
                     // Image
                     thumbContent = (
                        <img
                          src={item.url}
                          alt={`Miniature ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                     );
                 }

                 return (
                    <button
                      key={index}
                      onClick={() => setCurrentIndex(index)}
                      className={`flex-shrink-0 w-20 h-20 rounded overflow-hidden transition-all relative ${
                        index === currentIndex
                          ? 'ring-2 ring-white scale-105'
                          : 'opacity-60 hover:opacity-100'
                      }`}
                    >
                      {thumbContent}
                      
                      {item.type === 'video' && (
                        <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-xs py-0.5 text-center">
                          Vidéo
                        </div>
                      )}
                    </button>
                 );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PhotoModal;